import React, { useMemo } from "react";

/**
 * Stylized dotted world map.
 * Renders a grid of dots, marking those inside approximate continent regions.
 * Pings are placed at hand-tuned coordinates over the same SVG viewbox.
 */

const VIEW_W = 1000;
const VIEW_H = 500;

// Approximate continent regions (rectangular masks). Coordinates are in viewbox units.
// Each region: [minX, minY, maxX, maxY, optional shape modifier function]
const REGIONS = [
  // North America body
  { kind: "rect", x: 90, y: 90, w: 180, h: 130 },
  // Canada north + Alaska
  { kind: "rect", x: 60, y: 50, w: 220, h: 60 },
  // Mexico / Central America
  { kind: "rect", x: 180, y: 200, w: 90, h: 50 },
  // South America
  { kind: "tri", apex: [260, 240], base: [220, 440], width: 130 },
  // Greenland
  { kind: "rect", x: 340, y: 35, w: 70, h: 55 },
  // Europe
  { kind: "rect", x: 460, y: 90, w: 110, h: 90 },
  // Scandinavia
  { kind: "rect", x: 480, y: 50, w: 70, h: 50 },
  // North Africa
  { kind: "rect", x: 470, y: 200, w: 130, h: 80 },
  // Sub-Saharan Africa
  { kind: "rect", x: 490, y: 270, w: 110, h: 110 },
  // Middle East
  { kind: "rect", x: 580, y: 170, w: 80, h: 70 },
  // Russia / Northern Asia
  { kind: "rect", x: 560, y: 60, w: 280, h: 80 },
  // Central Asia
  { kind: "rect", x: 620, y: 130, w: 180, h: 60 },
  // East Asia (China)
  { kind: "rect", x: 720, y: 170, w: 110, h: 80 },
  // India subcontinent
  { kind: "rect", x: 690, y: 220, w: 60, h: 60 },
  // SE Asia / Indonesia
  { kind: "rect", x: 780, y: 250, w: 80, h: 50 },
  // Japan/Korea
  { kind: "rect", x: 830, y: 170, w: 30, h: 60 },
  // Australia
  { kind: "rect", x: 820, y: 330, w: 100, h: 70 },
  // New Zealand
  { kind: "rect", x: 940, y: 400, w: 25, h: 30 },
  // UK / Ireland
  { kind: "rect", x: 440, y: 110, w: 30, h: 35 },
];

function inRegion(x, y) {
  for (const r of REGIONS) {
    if (r.kind === "rect") {
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
        // soften western edges: jitter using a small noise based on coords
        return true;
      }
    } else if (r.kind === "tri") {
      // narrowing triangle for South America
      const [ax, ay] = r.apex;
      const [bx, by] = r.base;
      if (y >= ay && y <= by) {
        const t = (y - ay) / (by - ay);
        const halfW = r.width * (1 - t * 0.55) * 0.5;
        const cx = ax + (bx - ax) * t;
        if (Math.abs(x - cx) <= halfW) return true;
      }
    }
  }
  return false;
}

// Active and future location pings, positioned over the same viewbox.
// Active: São Paulo, Turin, Brussels.
// Future / expansion: generic regions only — LATAM, Africa, India.
const PINGS = [
  // Current bases
  { id: "turin", x: 504, y: 152, label: "Turin", status: "active", labelDir: "right" },
  { id: "brussels", x: 488, y: 122, label: "Brussels", status: "active", labelDir: "right" },
  { id: "saopaulo", x: 290, y: 360, label: "São Paulo", status: "active", labelDir: "right" },
  // Expansion roadmap — regional only
  { id: "latam", x: 230, y: 280, label: "LATAM", status: "future", year: "Expansion", labelDir: "left" },
  { id: "africa", x: 530, y: 320, label: "Africa", status: "future", year: "Expansion", labelDir: "right" },
  { id: "india", x: 720, y: 250, label: "India", status: "future", year: "Expansion", labelDir: "right" },
];

export const WorldMap = () => {
  const dots = useMemo(() => {
    const stepX = 11;
    const stepY = 11;
    const out = [];
    for (let y = 30; y < VIEW_H - 20; y += stepY) {
      for (let x = 40; x < VIEW_W - 30; x += stepX) {
        // jitter for organic feel
        const jx = ((x * 13 + y * 7) % 5) - 2;
        const jy = ((x * 5 + y * 11) % 5) - 2;
        if (inRegion(x, y)) {
          out.push({ x: x + jx, y: y + jy });
        }
      }
    }
    return out;
  }, []);

  return (
    <section
      id="world"
      data-testid="world-map-section"
      className="relative bg-brand-deeper text-white py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-3">
            <span className="overline text-[#2EC4B6]">— Where we are</span>
          </div>
          <div className="md:col-span-9">
            <h2
              className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05]"
              data-testid="world-map-heading"
            >
              From São Paulo to Brussels,<br />
              <span className="text-white/50">building toward a</span>{" "}
              <span className="text-[#2EC4B6]">global presence.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-lg text-white/60 leading-relaxed">
              Three operating bases today; six new offices on the roadmap. Every
              dot below marks a market where MAGIS is already shaping change or
              about to.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-12 relative">
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
          role="img"
          aria-label="Map of MAGIS global locations"
          data-testid="world-map-svg"
        >
          {/* land dots */}
          {dots.map((d, i) => (
            <circle
              key={i}
              cx={d.x}
              cy={d.y}
              r={2.1}
              fill="#1f4f54"
              opacity={0.85}
            />
          ))}

          {/* Pings */}
          {PINGS.map((p) => {
            const isActive = p.status === "active";
            return (
              <g key={p.id} data-testid={`ping-${p.id}`}>
                {/* pulse */}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={isActive ? 6 : 4.5}
                  fill="#2EC4B6"
                  opacity={isActive ? 0.35 : 0.2}
                  className={isActive ? "ping-soft" : "ping-soft-slow"}
                  style={{ transformOrigin: `${p.x}px ${p.y}px`, transformBox: "fill-box" }}
                />
                {/* core */}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={isActive ? 4.2 : 3}
                  fill={isActive ? "#2EC4B6" : "#FFFFFF"}
                  stroke={isActive ? "#FFFFFF" : "#2EC4B6"}
                  strokeWidth={isActive ? 1 : 1.2}
                />
                {/* label */}
                {(() => {
                  const isLeft = p.labelDir === "left";
                  const labelX = isLeft ? p.x - 10 : p.x + 10;
                  const anchor = isLeft ? "end" : "start";
                  return (
                    <>
                      <text
                        x={labelX}
                        y={p.y - 6}
                        fontSize="10"
                        textAnchor={anchor}
                        fill={isActive ? "#FFFFFF" : "rgba(255,255,255,0.78)"}
                        fontFamily="Satoshi, sans-serif"
                        fontWeight="600"
                      >
                        {p.label}
                      </text>
                      {!isActive && p.year && (
                        <text
                          x={labelX}
                          y={p.y + 6}
                          fontSize="7.5"
                          textAnchor={anchor}
                          fill="#2EC4B6"
                          fontFamily="Satoshi, sans-serif"
                          fontWeight="500"
                          letterSpacing="0.1em"
                        >
                          {p.year.toUpperCase()}
                        </text>
                      )}
                    </>
                  );
                })()}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend / list */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-light-translucent pt-10">
          <div data-testid="legend-active">
            <div className="flex items-center gap-3 mb-3">
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#2EC4B6] opacity-40 ping-soft" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#2EC4B6]" />
              </span>
              <span className="overline text-white">Active offices</span>
            </div>
            <p className="text-white/60 text-sm">São Paulo · Turin · Brussels</p>
          </div>
          <div data-testid="legend-future">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-3 w-3 rounded-full bg-white border border-[#2EC4B6]" />
              <span className="overline text-white">Expansion roadmap</span>
            </div>
            <p className="text-white/60 text-sm">LATAM · Africa · India</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorldMap;
